import React, { useState, useEffect, useCallback, useRef } from 'react';
import './free-grants.css';
import { formatDate } from './formatDate';

/**
 * "Grant free access" modal (feature 065-free-access-grants).
 *
 * ── THE RULE THIS COMPONENT EXISTS TO ENFORCE ────────────────────────────────
 *
 * Every date and every billing sentence shown here comes from
 * GET /admin/api/free-access-grants/preview. This component performs NO date
 * arithmetic and makes NO guess about what a store will do.
 *
 * That is not fussiness. The end date depends on the member's remaining trial,
 * their paid term, whether they have ever trialled, and Apple's per-member
 * annual quota — none of which the browser can see. A "helpful" client-side
 * estimate would disagree with what the server actually writes, and the admin
 * would be promising a member something different from what they get.
 *
 * There is deliberately NO plan picker: a grant extends whatever the member
 * already has. The admin only ever chooses a length and types a reason.
 *
 * `source` is optional and is the ONLY thing that varies by host screen. It is passed straight
 * to the API and decides the wording of the member's push notification; a host that omits it
 * gets the general wording. Nothing about the grant itself depends on it.
 *
 * `defaultDays` is optional too: a survey host passes the length its survey promises, so the
 * modal opens on that length rather than on 3 months. It is only a starting point — the admin
 * can still pick any other length.
 */

const PRESETS = [
  { label: '1 month', days: 30 },
  { label: '3 months', days: 90 },
  { label: '6 months', days: 180 },
  { label: '12 months', days: 365 },
  { label: 'Lifetime', days: null, isLifetime: true },
];

const MAX_REASON = 500;

// ── THE REASON PREFILL IS PER MEMBER, AND THAT IS THE WHOLE POINT ────────────────────────────
//
// `preview.last_reason` is the reason typed on THIS member's most recent grant, read from
// free_access_grant by the server (freeAccessGrantService.lastGrantReason). Revoked and expired
// grants count: the question is "what did we last say about this member", not "is their access
// live".
//
// It used to be one sessionStorage key — the last reason typed in this browser session, for ANY
// member. Opening the modal on the next member offered the previous member's sentence, and
// accepting it filed one person's justification as another person's audit record. A prefill that
// is wrong is worse than no prefill, because it is the field an admin is least likely to re-read.
//
// Applied ONCE per opening and never over text already typed: the preview reloads on every
// duration change, and a prefill that reasserted itself would overwrite the admin mid-sentence.

const BANNER_CLASS = {
  paid: 'fg-state fg-state--paid',
  trial: 'fg-state fg-state--trial',
  expired_trial: 'fg-state fg-state--free',
  free: 'fg-state fg-state--free',
};

export default function GrantAccessModal({ user, onClose, onGranted, api, source, defaultDays }) {
  const defaultPreset = defaultDays ? PRESETS.find((p) => p.days === defaultDays) : PRESETS[1];
  const [selected, setSelected] = useState(defaultPreset || PRESETS[1]);
  const [customDays, setCustomDays] = useState(defaultPreset ? '' : String(defaultDays));
  const [useCustom, setUseCustom] = useState(!defaultPreset);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const prefilled = useRef(false);

  const isLifetime = Boolean(selected.isLifetime) && !useCustom;
  const days = useCustom ? parseInt(customDays, 10) : selected.days;
  const durationValid = isLifetime || (Number.isInteger(days) && days >= 1 && days <= 3650);

  const loadPreview = useCallback(async (isCurrent) => {
    if (!user || !durationValid) { setPreview(null); return; }
    setLoadingPreview(true);
    setPreviewError('');
    try {
      const data = await api.preview({
        user_id: user.id,
        ...(isLifetime ? { is_lifetime: 1 } : { duration_days: days }),
      });
      if (!isCurrent()) return;
      setPreview(data);
      if (!prefilled.current) {
        prefilled.current = true;
        if (data?.last_reason) setReason((typed) => (typed.trim() ? typed : data.last_reason));
      }
    } catch (err) {
      if (!isCurrent()) return;
      setPreview(null);
      setPreviewError(err?.message || 'Could not read this member’s current plan.');
    } finally {
      if (isCurrent()) setLoadingPreview(false);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [user, days, isLifetime, durationValid]);

  // The guard is not cosmetic: the admin can change the duration (or close the modal) while a
  // preview is still in flight, and a late response must not overwrite the newer one — that would
  // show a date belonging to a duration they are no longer choosing.
  useEffect(() => {
    let live = true;
    loadPreview(() => live);
    return () => { live = false; };
  }, [loadPreview]);

  const reasonValid = reason.trim().length > 0 && reason.length <= MAX_REASON;
  const canSubmit = durationValid && reasonValid && !submitting && !loadingPreview;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const grant = await api.create({
        user_id: user.id,
        ...(isLifetime ? { is_lifetime: true } : { duration_days: days }),
        reason: reason.trim(),
        // Which screen this grant was started from. It changes nothing about the entitlement —
        // it only tells the server which wording the member's push should carry, because a
        // grant made from a survey screen is thanking them for that survey.
        ...(source ? { source } : {}),
      });
      onGranted(grant);
    } catch (err) {
      setSubmitError(err?.message || 'Could not save the grant. Nothing was changed.');
    } finally {
      setSubmitting(false);
    }
  };

  const banner = preview
    ? (BANNER_CLASS[preview.state] || BANNER_CLASS.free)
    : 'fg-state fg-state--free';

  // "Extend trial by" / "Extend subscription by" — the grant extends what they already have.
  const durationLegend = preview?.state === 'paid'
    ? 'Extend subscription by'
    : preview?.state === 'trial'
      ? 'Extend trial by'
      : 'Give free access for';

  return (
    <div className="fg-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="fg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fg-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fg-modal-head">
          <h6 id="fg-modal-title">Grant free access</h6>
          <p>{user?.name} {user?.email ? `· ${user.email}` : ''}</p>
        </div>

        <div className="fg-modal-body">
          {previewError && <div className="fg-alert fg-alert--error">{previewError}</div>}

          {preview && (
            <div className={banner} data-testid="fg-state-banner">
              <b>{preview.state_label}</b>
              <span>{preview.state_detail}</span>
            </div>
          )}

          <div className="fg-field">
            <label htmlFor="fg-duration">{durationLegend}</label>
            <div className="fg-presets" role="group" aria-label={durationLegend}>
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className={
                    !useCustom && selected.label === p.label
                      ? 'fg-preset fg-preset--on'
                      : 'fg-preset'
                  }
                  onClick={() => { setSelected(p); setUseCustom(false); }}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                className={useCustom ? 'fg-preset fg-preset--on' : 'fg-preset'}
                onClick={() => setUseCustom(true)}
              >
                Custom
              </button>
            </div>

            {useCustom && (
              <input
                id="fg-duration"
                className="fg-input"
                type="number"
                min="1"
                max="3650"
                placeholder="Number of days (1–3650)"
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
              />
            )}
          </div>

          <div className="fg-field">
            <label htmlFor="fg-reason">Reason (required)</label>
            <input
              id="fg-reason"
              className="fg-input"
              type="text"
              maxLength={MAX_REASON}
              placeholder="e.g. Support goodwill — ticket #4821"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {preview?.last_reason && reason === preview.last_reason && (
              <p className="fg-hint" data-testid="fg-reason-prefill">
                Carried over from this member’s last grant
                {preview.last_reason_at ? ` on ${formatDate(preview.last_reason_at)}` : ''}. Edit it if this one is different.
              </p>
            )}
          </div>

          {loadingPreview && <div className="fg-preview fg-preview--loading">Working it out…</div>}

          {preview && !loadingPreview && (
            <div className="fg-preview" data-testid="fg-preview">
              <div className="fg-preview-row">
                <span className="fg-k">
                  {preview.is_lifetime ? 'Access' : 'Access until'}
                </span>
                <b>{preview.is_lifetime ? 'Forever' : formatDate(preview.access_until)}</b>
              </div>
              <div className="fg-preview-row">
                <span className="fg-k">Worked out as</span>
                <span>{preview.worked_out_as}</span>
              </div>
              <div className="fg-preview-row">
                <span className="fg-k">Billing</span>
                <span>{preview.billing_plan?.message}</span>
              </div>
            </div>
          )}

          {preview?.warning && (
            <div className="fg-alert fg-alert--warn" data-testid="fg-warning">
              {preview.warning}
            </div>
          )}

          {submitError && <div className="fg-alert fg-alert--error">{submitError}</div>}
        </div>

        <div className="fg-modal-foot">
          <button type="button" className="fg-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="fg-btn fg-btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Granting…' : 'Grant access'}
          </button>
        </div>
      </div>
    </div>
  );
}

// formatDate now lives in ./formatDate.js — re-exported here so existing importers keep working.
export { formatDate };
